import type { FilterState } from '../../components/action-bar/filter-menu';
import { OrderByEnum } from '../../data-grid.type';
import {
	URL_SYNC_CALENDAR_DATE,
	URL_SYNC_CONTEXT_PARAM,
	URL_SYNC_DATE_FROM_PARAM,
	URL_SYNC_DATE_TO_PARAM,
	URL_SYNC_HIDE_COLS_PARAM,
	URL_SYNC_MIN_PAGE,
	URL_SYNC_ORDER_BY_PARAM,
	URL_SYNC_ORDER_PARAM,
	URL_SYNC_PAGE_PARAM,
	URL_SYNC_RESERVED_PARAMS,
	URL_SYNC_SEARCH_PARAM,
} from './constants';
import type {
	IContextualizeDataGridUrlSearchInput,
	IDataGridUrlCalendarDates,
	IDataGridUrlColumnMeta,
	IDataGridUrlState,
	IParseDataGridUrlDateRangeInput,
	IParseDataGridUrlSearchInput,
	ISerializeDataGridUrlSearchInput,
} from './types';

export function prefixParam(paramPrefix: string | undefined, key: string): string {
	if (!paramPrefix) {
		return key;
	}
	return `${paramPrefix}${key}`;
}

export function stripLeadingQuestion(search: string): string {
	if (search.startsWith('?')) {
		return search.slice(1);
	}
	return search;
}

function parseSearchParams(search: string): URLSearchParams {
	return new URLSearchParams(stripLeadingQuestion(search));
}

function getPrefixedReservedKeys(paramPrefix: string | undefined): Set<string> {
	return new Set(URL_SYNC_RESERVED_PARAMS.map(key => prefixParam(paramPrefix, key)));
}

function getFilterParamNames(columns: IDataGridUrlColumnMeta[], paramPrefix: string | undefined): Set<string> {
	return new Set(columns.map(column => prefixParam(paramPrefix, column.paramName)));
}

export function parseCsv(value: string | null): string[] {
	if (!value) {
		return [];
	}
	return value
		.split(',')
		.map(part => part.trim())
		.filter(Boolean)
		.map(part => {
			try {
				return decodeURIComponent(part);
			} catch {
				return part;
			}
		});
}

function serializeCsv(values: string[]): string {
	return values.map(value => encodeURIComponent(value)).join(',');
}

/**
 * Compares two search strings by key/value pairs so param ordering is insignificant.
 */
export function areSearchStringsEqual(a: string, b: string): boolean {
	const paramsA = parseSearchParams(a);
	const paramsB = parseSearchParams(b);
	const keysA = [...new Set(paramsA.keys())].sort();
	const keysB = [...new Set(paramsB.keys())].sort();
	if (keysA.length !== keysB.length) {
		return false;
	}
	return keysA.every((key, index) => {
		if (key !== keysB[index]) {
			return false;
		}
		const valuesA = paramsA.getAll(key).sort();
		const valuesB = paramsB.getAll(key).sort();
		return isSameStringArray(valuesA, valuesB);
	});
}

function parsePositiveInt(value: string | null): number | undefined {
	if (value == null || !/^[1-9]\d*$/.test(value)) {
		return undefined;
	}
	const parsed = Number.parseInt(value, 10);
	if (!Number.isSafeInteger(parsed)) {
		return undefined;
	}
	return parsed;
}

function parseOrder(value: string | null, fallback: OrderByEnum): OrderByEnum {
	if (value === OrderByEnum.ASC || value === OrderByEnum.DESC) {
		return value;
	}
	return fallback;
}

export function contextualizeDataGridUrlSearch({
	search,
	paramPrefix,
	contextKey,
}: IContextualizeDataGridUrlSearchInput): string {
	const params = parseSearchParams(search);
	const contextParam = prefixParam(paramPrefix, URL_SYNC_CONTEXT_PARAM);
	const urlContext = params.get(contextParam);
	if (urlContext == null || urlContext === contextKey) {
		return params.toString();
	}
	for (const key of Array.from(params.keys())) {
		if (key.startsWith(paramPrefix)) {
			params.delete(key);
		}
	}
	return params.toString();
}

/**
 * Calendar date for the query string. Datetimes are rejected because their calendar day depends
 * on the consumer's timezone and DataGrid does not own that context. Real-day checks come from
 * the app via `calendarDates`.
 */
export function toUrlCalendarDate(
	value: string | undefined | null,
	calendarDates?: IDataGridUrlCalendarDates
): string {
	if (!value || !isCalendarDate(value, calendarDates)) {
		return '';
	}
	return value;
}

function isCalendarDate(value: string, calendarDates?: IDataGridUrlCalendarDates): boolean {
	if (calendarDates) {
		return calendarDates.isCalendarDate(value);
	}
	return URL_SYNC_CALENDAR_DATE.test(value);
}

function isFromAfterTo(from: string, to: string, calendarDates?: IDataGridUrlCalendarDates): boolean {
	if (calendarDates) {
		return calendarDates.isFromAfterTo(from, to);
	}
	return from > to;
}

interface IParseCalendarDateRangeInput {
	dateFrom: string | null;
	dateTo: string | null;
	defaultDateFrom: string;
	defaultDateTo: string;
	calendarDates?: IDataGridUrlCalendarDates;
}

function resolveCalendarDateRange({
	dateFrom,
	dateTo,
	defaultDateFrom,
	defaultDateTo,
	calendarDates,
}: IParseCalendarDateRangeInput): Pick<IDataGridUrlState, 'dateFrom' | 'dateTo'> {
	if (dateFrom == null && dateTo == null) {
		return { dateFrom: defaultDateFrom, dateTo: defaultDateTo };
	}
	if (
		dateFrom == null ||
		dateTo == null ||
		!isCalendarDate(dateFrom, calendarDates) ||
		!isCalendarDate(dateTo, calendarDates) ||
		isFromAfterTo(dateFrom, dateTo, calendarDates)
	) {
		return { dateFrom: defaultDateFrom, dateTo: defaultDateTo };
	}
	return { dateFrom, dateTo };
}

export function parseDataGridUrlDateRange({
	search,
	paramPrefix,
	defaultDateFrom,
	defaultDateTo,
	calendarDates,
}: IParseDataGridUrlDateRangeInput): Pick<IDataGridUrlState, 'dateFrom' | 'dateTo'> {
	const params = parseSearchParams(search);
	return resolveCalendarDateRange({
		dateFrom: params.get(prefixParam(paramPrefix, URL_SYNC_DATE_FROM_PARAM)),
		dateTo: params.get(prefixParam(paramPrefix, URL_SYNC_DATE_TO_PARAM)),
		defaultDateFrom,
		defaultDateTo,
		calendarDates,
	});
}

/**
 * Parses the browser search string into DataGrid URL state, ignoring unknown / foreign params.
 */
export function parseDataGridUrlSearch(input: IParseDataGridUrlSearchInput): IDataGridUrlState {
	const { search, paramPrefix, columns, defaults, sortableColumnKeys, calendarDates } = input;
	const params = parseSearchParams(search);

	const page = parsePositiveInt(params.get(prefixParam(paramPrefix, URL_SYNC_PAGE_PARAM)));
	const searchValue = params.get(prefixParam(paramPrefix, URL_SYNC_SEARCH_PARAM)) ?? defaults.search;
	const order = parseOrder(params.get(prefixParam(paramPrefix, URL_SYNC_ORDER_PARAM)), defaults.order);
	const rawOrderBy = params.get(prefixParam(paramPrefix, URL_SYNC_ORDER_BY_PARAM));
	const orderBy =
		rawOrderBy != null && (sortableColumnKeys == null || sortableColumnKeys.includes(rawOrderBy))
			? rawOrderBy
			: defaults.orderBy;
	const rawHiddenColumns = params.get(prefixParam(paramPrefix, URL_SYNC_HIDE_COLS_PARAM));
	const hiddenColumnKeys = rawHiddenColumns == null ? defaults.hiddenColumnKeys : parseCsv(rawHiddenColumns);
	const { dateFrom, dateTo } = parseDataGridUrlDateRange({
		search,
		paramPrefix,
		defaultDateFrom: defaults.dateFrom,
		defaultDateTo: defaults.dateTo,
		calendarDates,
	});

	const filters: Record<string, string[]> = Object.fromEntries(
		Object.entries(defaults.filters).map(([paramName, values]) => [paramName, [...values]])
	);

	for (const column of columns) {
		const rawValue = params.get(prefixParam(paramPrefix, column.paramName));
		if (rawValue == null) {
			continue;
		}
		const values = parseCsv(rawValue);
		if (values.length === 0) {
			delete filters[column.paramName];
			continue;
		}
		filters[column.paramName] = values;
	}

	return {
		pageIndex: page != null ? Math.max(0, page - URL_SYNC_MIN_PAGE) : defaults.pageIndex,
		search: searchValue,
		order,
		orderBy,
		filters,
		hiddenColumnKeys,
		dateFrom,
		dateTo,
	};
}

interface IClearOwnedParamsInput {
	params: URLSearchParams;
	paramPrefix: string | undefined;
	columns: IDataGridUrlColumnMeta[];
}

function clearOwnedParams({ params, paramPrefix, columns }: IClearOwnedParamsInput): void {
	if (paramPrefix) {
		for (const key of [...params.keys()]) {
			if (key.startsWith(paramPrefix)) {
				params.delete(key);
			}
		}
		return;
	}
	for (const key of getPrefixedReservedKeys(paramPrefix)) {
		params.delete(key);
	}
	for (const key of getFilterParamNames(columns, paramPrefix)) {
		params.delete(key);
	}
}

function isSameStringArray(a: string[], b: string[]): boolean {
	if (a.length !== b.length) {
		return false;
	}
	return a.every((value, index) => value === b[index]);
}

/**
 * Writes grid-owned params into the current search string while preserving foreign keys.
 * Omits values that match defaults. An empty hidden-columns value is written (rather than omitted)
 * when the grid hides columns by default, so "every column visible" survives a reload.
 */
export function serializeDataGridUrlSearch(input: ISerializeDataGridUrlSearchInput): string {
	const { currentSearch, paramPrefix, columns, defaults, state } = input;
	const params = parseSearchParams(currentSearch);
	clearOwnedParams({ params, paramPrefix, columns });

	const pageOneBased = state.pageIndex + URL_SYNC_MIN_PAGE;
	if (pageOneBased !== defaults.pageIndex + URL_SYNC_MIN_PAGE) {
		params.set(prefixParam(paramPrefix, URL_SYNC_PAGE_PARAM), String(pageOneBased));
	}

	if (state.search !== defaults.search) {
		const searchKey = prefixParam(paramPrefix, URL_SYNC_SEARCH_PARAM);
		if (state.search) {
			params.set(searchKey, state.search);
		} else {
			params.delete(searchKey);
		}
	}

	if (state.order !== defaults.order) {
		params.set(prefixParam(paramPrefix, URL_SYNC_ORDER_PARAM), state.order);
	}

	if (state.orderBy !== defaults.orderBy) {
		params.set(prefixParam(paramPrefix, URL_SYNC_ORDER_BY_PARAM), state.orderBy);
	}

	const sortedHidden = [...state.hiddenColumnKeys].sort();
	const sortedDefaultHidden = [...defaults.hiddenColumnKeys].sort();
	if (!isSameStringArray(sortedHidden, sortedDefaultHidden)) {
		params.set(prefixParam(paramPrefix, URL_SYNC_HIDE_COLS_PARAM), serializeCsv(sortedHidden));
	}

	const hasChangedDateRange = state.dateFrom !== defaults.dateFrom || state.dateTo !== defaults.dateTo;
	if (hasChangedDateRange && state.dateFrom && state.dateTo) {
		params.set(prefixParam(paramPrefix, URL_SYNC_DATE_FROM_PARAM), state.dateFrom);
		params.set(prefixParam(paramPrefix, URL_SYNC_DATE_TO_PARAM), state.dateTo);
	}

	for (const column of columns) {
		const values = state.filters[column.paramName];
		const defaultValues = defaults.filters[column.paramName] ?? [];
		const filterKey = prefixParam(paramPrefix, column.paramName);
		if (!values) {
			if (defaultValues.length > 0) {
				params.set(filterKey, '');
			}
			continue;
		}
		const sortedValues = [...values].sort();
		const sortedDefaults = [...defaultValues].sort();
		if (isSameStringArray(sortedValues, sortedDefaults)) {
			continue;
		}
		params.set(filterKey, serializeCsv(values));
	}

	return params.toString();
}

const reservedParamNames: ReadonlySet<string> = new Set(URL_SYNC_RESERVED_PARAMS);
const warnedReservedParamNames = new Set<string>();

/**
 * Builds the URL param metadata for the grid's filterable columns.
 *
 * A filter whose param name collides with a reserved key (`page`, `search`, `order`, ...) is
 * excluded from URL sync: claiming it would let a filter value overwrite pagination or sorting in
 * both the URL and the API payload. The filter still works in-grid, it just is not persisted.
 */
export function buildColumnMetaFromDefs(
	columns: Array<{
		key: string;
		filter?: { initial?: string[] | number[]; paramName?: string };
		type?: string;
	}>
): IDataGridUrlColumnMeta[] {
	return columns
		.filter(column => column.type !== 'actions' && Boolean(column.filter))
		.map(column => ({
			defaultValues: column.filter?.initial?.map(String) ?? [],
			key: column.key,
			paramName: column.filter?.paramName ?? column.key,
		}))
		.filter(column => {
			if (!reservedParamNames.has(column.paramName)) {
				return true;
			}
			if (!warnedReservedParamNames.has(column.paramName)) {
				warnedReservedParamNames.add(column.paramName);
				console.warn(
					`DataGrid url sync: filter column "${column.key}" uses the reserved param name ` +
						`"${column.paramName}" and will not be synced to the URL. Set a different ` +
						'filter.paramName to enable sync for this column.'
				);
			}
			return false;
		});
}

/**
 * Converts URL filter values into the grid's active-filter shape. Display labels are left empty
 * because the URL only carries option values; they are resolved once filter options have loaded.
 *
 * Groups with no selected values are dropped: the grid treats an empty group as no filter at all,
 * and an empty group here would read as "the user chose these filters" and suppress the grid's
 * own defaults.
 */
export function filtersRecordToActiveShape(
	filters: Record<string, string[]>,
	columns: IDataGridUrlColumnMeta[]
): Record<string, FilterState> {
	const paramToKey = new Map(columns.map(column => [column.paramName, column.key]));
	const result: Record<string, FilterState> = {};

	for (const [paramName, values] of Object.entries(filters)) {
		if (values.length === 0) {
			continue;
		}
		const groupKey = paramToKey.get(paramName) ?? paramName;
		result[groupKey] = {
			paramName,
			values: new Set(values),
			labels: new Map<string, string>(),
		};
	}

	return result;
}

export function activeFiltersToUrlFilters(
	activeFilters: Record<string, { paramName?: string; values: Set<string> }>
): Record<string, string[]> {
	const filters: Record<string, string[]> = {};
	for (const [groupKey, filter] of Object.entries(activeFilters)) {
		const paramName = filter.paramName || groupKey;
		filters[paramName] = Array.from(filter.values).map(String);
	}
	return filters;
}
